import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Mail, Lock, User, UserPlus, Briefcase } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useAuthForm } from '@/contexts/AuthFormProvider';
import { usePendingProject } from '@/contexts/PendingProjectProvider';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '@/config';
import { apiCreateProjectList, ProjectCreationResponse } from '@/services/ProjectService';

const RegisterSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
  role: z.string().min(1, 'Please select your role')
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterFormData = z.infer<typeof RegisterSchema>;

const RegisterForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const { toast } = useToast();
  const { showLoginForm, closeForm } = useAuthForm();
  const { pendingProject } = usePendingProject();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<RegisterFormData>({
    resolver: zodResolver(RegisterSchema)
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError(''); // Clear previous errors
    try {
      // Register the user
      const registerRes = await axios.post(
        `${config.NEXT_PUBLIC_BASE_URL}/auth/register`,
        { 
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          password: data.password,
          role: data.role
        },
        {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      if (registerRes.status === 201) {
        // Auto-login after successful registration
        try {
          const loginRes = await axios.post(
            `${config.NEXT_PUBLIC_BASE_URL}/auth/login`,
            { email: data.email, password: data.password },
            {
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
              },
            }
          );

          if (loginRes.status === 200 && loginRes.data.accessToken) {
            localStorage.setItem('accessToken', loginRes.data.accessToken);

            // Track Google Ads conversion
            const transactionId = `reg_${Date.now()}_${data.email.split('@')[0]}`;
            console.log('[Google Ads] Attempting conversion tracking...');
            console.log('[Google Ads] gtag available:', typeof window.gtag !== 'undefined');
            console.log('[Google Ads] dataLayer:', window.dataLayer);

            if (window.gtag) {
              console.log('[Google Ads] Firing conversion event with:', {
                send_to: 'AW-17073712169/C8p5CN-dmqUbEKnYsM0_',
                transaction_id: transactionId
              });

              window.gtag('event', 'conversion', {
                'send_to': 'AW-17073712169/C8p5CN-dmqUbEKnYsM0_',
                'transaction_id': transactionId
              });

              console.log('[Google Ads] Conversion event fired successfully');
            } else {
              console.error('[Google Ads] gtag is not available - conversion not tracked');
            }

            toast({
              title: 'Welcome to ShredsAI!',
              description: 'Your account has been created successfully.',
            });
            closeForm();

            // Check if there's a pending project from home page
            if (pendingProject) {
              console.log('[RegisterForm] Pending project detected, creating project...', pendingProject);
              try {
                // Create the project
                const projectRes = await apiCreateProjectList<ProjectCreationResponse, { projectName: string; mainPackage: string }>({
                  projectName: pendingProject.projectName,
                  mainPackage: 'ai.shreds'
                });

                if (projectRes.data && projectRes.data.id) {
                  console.log('[RegisterForm] Project created successfully:', projectRes.data.id);
                  // Navigate to the generate page with the project ID and prompt in state
                  navigate(`/architecture/generate/${projectRes.data.id}`, {
                    state: {
                      autoSubmitPrompt: pendingProject.prompt,
                      fromHomePage: true
                    }
                  });
                  return; // Exit early, don't navigate to dashboard
                }
              } catch (projectError) {
                console.error('[RegisterForm] Failed to create project:', projectError);
                toast({
                  title: 'Project Creation Failed',
                  description: 'Could not create your project. Please try again from the dashboard.',
                  variant: 'destructive'
                });
              }
            }

            // Default: navigate to dashboard if no pending project
            navigate('/dashboard');
          }
        } catch (loginError) {
          toast({
            title: 'Automatic Login Failed',
            description: 'Please log in manually.',
            variant: 'destructive'
          });
          console.error('Auto-login error:', loginError);
        }
      }
    } catch (error: any) {
      // Display detailed error message like original Demo.tsx
      const errorMessage = (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.length > 0)
        ? error.response.data.errors.join(", ")
        : (error?.response?.data?.message || "Registration failed. Please try again later.");
      
      setError(errorMessage);
      console.error('Registration error:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="w-full max-w-md mx-auto bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}
    >
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <motion.h2 
          className="text-2xl sm:text-3xl font-bold text-white mb-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Join ShredsAI
        </motion.h2>
        <motion.p 
          className="text-sm sm:text-base text-gray-300 px-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          Create your account to get started
        </motion.p>
      </div>

      {/* Error Display */}
      {error && (
        <motion.div
          className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p className="text-red-400 text-sm font-medium">{error}</p>
        </motion.div>
      )}

      {/* Form */}
      <motion.form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 sm:space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >

        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-200">First Name</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                {...register('firstName')}
                type="text"
                className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-base"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '16px',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
                placeholder="First name"
              />
            </div>
            {errors.firstName && (
              <p className="text-red-400 text-xs">{errors.firstName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-200">Last Name</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                {...register('lastName')}
                type="text"
                className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-base"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '16px',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
                placeholder="Last name"
              />
            </div>
            {errors.lastName && (
              <p className="text-red-400 text-xs">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        {/* Email Field */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-200">Email</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              {...register('email')}
              type="email"
              className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-base"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
              placeholder="Enter your email"
            />
          </div>
          {errors.email && (
            <p className="text-red-400 text-sm">{errors.email.message}</p>
          )}
        </div>

        {/* Password Field */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-200">Password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-base"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
              placeholder="Create a password"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors duration-200"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-400 text-sm">{errors.password.message}</p>
          )}
        </div>

        {/* Confirm Password Field */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-200">Confirm Password</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              {...register('confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-base"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors duration-200"
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-red-400 text-sm">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Role Field */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-200">Role *</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Briefcase className="h-5 w-5 text-gray-400" />
            </div>
            <select
              {...register('role')}
              className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 appearance-none cursor-pointer"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
            >
              <option value="" className="bg-gray-800 text-gray-300">Select your role</option>
              <option value="developer" className="bg-gray-800 text-white">Software Developer</option>
              <option value="architect" className="bg-gray-800 text-white">Software Architect</option>
              <option value="lead" className="bg-gray-800 text-white">Tech Lead</option>
              <option value="manager" className="bg-gray-800 text-white">Engineering Manager</option>
              <option value="cto" className="bg-gray-800 text-white">CTO</option>
              <option value="founder" className="bg-gray-800 text-white">Founder</option>
              <option value="other" className="bg-gray-800 text-white">Other</option>
            </select>
            {/* Custom dropdown arrow */}
            <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          {errors.role && (
            <p className="text-red-400 text-sm">{errors.role.message}</p>
          )}
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 sm:py-4 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold text-base sm:text-lg rounded-xl hover:from-blue-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2"
          whileHover={{ scale: isLoading ? 1 : 1.02 }}
          whileTap={{ scale: isLoading ? 1 : 0.98 }}
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <UserPlus className="w-5 h-5" />
              <span>Create Account</span>
            </>
          )}
        </motion.button>

        {/* Switch to Login */}
        <div className="text-center">
          <p className="text-gray-300">
            Already have an account?{' '}
            <button
              type="button"
              onClick={showLoginForm}
              className="text-blue-400 hover:text-blue-300 font-semibold transition-colors duration-200"
            >
              Sign in
            </button>
          </p>
        </div>
      </motion.form>

      {/* Terms */}
      <motion.p
        className="text-xs text-gray-400 text-center mt-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        By creating an account, you agree to our{' '}
        <a href="/privacy" className="text-blue-400 hover:text-blue-300">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="/privacy" className="text-blue-400 hover:text-blue-300">
          Privacy Policy
        </a>
        .
      </motion.p>
    </div>
  );
};

export default RegisterForm;